import { useEffect, useMemo, useState } from "react";
import { useDataChangedTick } from "../useDataChanged";
import { AppShell } from "../components/AppShell";
import { OverviewTopbar, ProjectsRow } from "../components/OverviewTopbar";
import { ScanStrip } from "../components/ScanStrip";
import { IconChevronDown } from "../components/icons";
import { CardSkeleton } from "../components/Skeletons";
import { useI18n, type TranslationKey } from "../i18n";
import {
  api,
  isProjectVerified,
  normalizeApiText,
  type AttackVector,
} from "../api";
import { useAppState } from "../AppStateContext";
import "./overview.css";
import "./hypoteses.css";

type Tab = "All" | "Analyze" | "Exploit" | "Finding" | "Rejected";

const TABS: { id: Tab; labelKey: TranslationKey }[] = [
  { id: "All", labelKey: "hypoteses.tabAll" },
  { id: "Analyze", labelKey: "hypoteses.tabAnalyze" },
  { id: "Exploit", labelKey: "hypoteses.tabExploit" },
  { id: "Finding", labelKey: "hypoteses.tabFinding" },
  { id: "Rejected", labelKey: "hypoteses.tabRejected" },
];

// Hypotheses ARE attack vectors — what the LLM proposed to test. The tabs map
// onto the vector lifecycle (pending → assigned → tested →
// tested_no_finding | confirmed | failed) so each vector lands in exactly one
// non-All tab (no double-counting across tabs like the old findings-based UI).
function matchesTab(tab: Tab, vector: AttackVector): boolean {
  const status = normalizeApiText(vector.status);
  switch (tab) {
    case "Analyze":
      return status === "pending" || status === "assigned";
    case "Exploit":
      return status === "tested";
    case "Finding":
      return status === "confirmed";
    case "Rejected":
      return status === "failed" || status === "tested_no_finding";
    default:
      return true;
  }
}

export default function HypotesesPage() {
  const { projects, activeProject, setActiveProjectId, activeScan, viewedScan } = useAppState();
  const dataTick = useDataChangedTick();
  const { t } = useI18n();
  const displayScan = viewedScan ?? activeScan;
  const [vectors, setVectors] = useState<AttackVector[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("All");
  const [expanded, setExpanded] = useState<string | null>(null);
  const activeScanId = displayScan?.scan_id ?? null;

  useEffect(() => {
    let cancelled = false;
    if (!activeScanId) {
      const resetId = window.setTimeout(() => {
        if (!cancelled) {
          setVectors([]);
          setExpanded(null);
          setError(null);
          setLoading(false);
        }
      }, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(resetId);
      };
    }
    const tick = async () => {
      try {
        const r = await api.listAttackVectors(activeScanId);
        if (!cancelled) setVectors(r.vectors);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t("hypoteses.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    setError(null);
    const initialId = window.setTimeout(() => void tick(), 0);
    const id = window.setInterval(tick, 10_000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialId);
      window.clearInterval(id);
    };
  }, [activeScanId, dataTick]);

  const filtered = useMemo(
    () => vectors.filter((v) => matchesTab(activeTab, v)),
    [vectors, activeTab],
  );

  const counts = useMemo<Record<Tab, number>>(
    () => ({
      All: vectors.length,
      Analyze: vectors.filter((v) => matchesTab("Analyze", v)).length,
      Exploit: vectors.filter((v) => matchesTab("Exploit", v)).length,
      Finding: vectors.filter((v) => matchesTab("Finding", v)).length,
      Rejected: vectors.filter((v) => matchesTab("Rejected", v)).length,
    }),
    [vectors],
  );

  return (
    <AppShell>
      <div className="overview anim-page">
        <OverviewTopbar />
        <ProjectsRow
          searches={projects.map((p) => ({
            id: p.project_id,
            label: p.name,
            verified: isProjectVerified(p),
          }))}
          activeId={activeProject?.project_id}
          onSelect={setActiveProjectId}
        />
        <ScanStrip />

        <div className="hyp__header">
          <p className="overview__section-label" style={{ margin: 0 }}>
            {t("hypoteses.sectionLabel")}
          </p>
          <div className="hyp__tabs">
            {TABS.map(({ id, labelKey }) => (
              <button
                key={id}
                type="button"
                className={`hyp__tab${activeTab === id ? " hyp__tab--active" : ""}`}
                onClick={() => setActiveTab(id)}
              >
                {t(labelKey)} ({counts[id]})
              </button>
            ))}
          </div>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}

        {loading && filtered.length === 0 ? (
          <div className="hyp__list">
            <CardSkeleton count={5} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="helper">
            {displayScan ? t("hypoteses.emptyFiltered") : t("hypoteses.emptyNoScan")}
          </p>
        ) : (
          <div
            className="hyp__list anim-stagger"
            key={`hyp-${activeTab}`}
            style={{ ["--anim-stagger-step" as never]: "50ms" }}
          >
            {filtered.map((v) => (
              <HypRow
                key={v.vector_id}
                vector={v}
                expanded={expanded === v.vector_id}
                onToggle={() =>
                  setExpanded((cur) => (cur === v.vector_id ? null : v.vector_id))
                }
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function HypRow({
  vector,
  expanded,
  onToggle,
}: {
  vector: AttackVector;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useI18n();
  const dotTone = severityDotTone(vector.severity);
  const status = formatStatusBadge(vector, t);
  const kind = vector.vuln_type?.toUpperCase() || t("hypoteses.findingKindDefault");
  const title = vector.target || vector.host || "—";
  const hasDetail = Boolean(vector.reason || vector.how_to_test);

  return (
    <article className={`hyp-row${expanded ? " hyp-row--expanded" : ""}`}>
      <div className="hyp-row__head">
        <div className="hyp-row__lead">
          <span className={`hyp-row__dot hyp-row__dot--${dotTone}`} />
          <span className="hyp-row__kind">{kind}</span>
          <span className="hyp-row__title">{title}</span>
          {status ? (
            <span className={`hyp-badge hyp-badge--${status.tone}`}>{status.text}</span>
          ) : null}
        </div>
        {hasDetail ? (
          <div className="hyp-row__actions">
            <button
              type="button"
              className="hyp-action hyp-action--ghost"
              onClick={onToggle}
            >
              {t("hypoteses.viewDetails")}
              <IconChevronDown
                width={14}
                height={14}
                style={{
                  transform: expanded ? "rotate(180deg)" : undefined,
                  transition: "transform 0.15s ease",
                }}
              />
            </button>
          </div>
        ) : null}
      </div>

      {expanded && hasDetail ? (
        <div className="hyp-row__body">
          {vector.reason ? (
            <div className="hyp-field">
              <p className="hyp-field__label">{t("hypoteses.fieldReason")}</p>
              <div className="hyp-field__value">
                <pre>{vector.reason}</pre>
              </div>
            </div>
          ) : null}
          {vector.how_to_test ? (
            <div className="hyp-field" style={{ marginTop: vector.reason ? 16 : 0 }}>
              <p className="hyp-field__label">{t("hypoteses.fieldHowToTest")}</p>
              <div className="hyp-field__value">
                <pre>{vector.how_to_test}</pre>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function severityDotTone(severity: string | undefined): "red" | "orange" | "blue" | "green" {
  const s = normalizeApiText(severity ?? "");
  if (s === "critical") return "red";
  if (s === "high") return "orange";
  if (s === "medium") return "blue";
  return "green";
}

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;

function formatStatusBadge(
  vector: AttackVector,
  t: TFn,
): { text: string; tone: "analyse" | "ready" | "rejected" | "default" } {
  // Badge reflects the attack-vector lifecycle status.
  const status = normalizeApiText(vector.status);
  if (status === "confirmed") {
    return { text: t("hypoteses.badgeFinding"), tone: "ready" };
  }
  if (status === "failed" || status === "tested_no_finding") {
    return { text: t("hypoteses.badgeRejected"), tone: "rejected" };
  }
  if (status === "tested") {
    return { text: t("hypoteses.badgeExploit"), tone: "default" };
  }
  return { text: t("hypoteses.badgeAnalyse"), tone: "analyse" };
}
